/**
 * Helpers for working with 360° equirectangular panoramas.
 *
 * - Reads GPano XMP metadata from JPEG (APP1) and PNG (iTXt) source files so
 *   we can preserve the original FullPanoWidth/Height when re-encoding the
 *   staged result.
 * - Builds a GPano XMP packet and injects it into PNG / JPEG byte streams so
 *   Facebook 360, Google Photos, and VR viewers still recognise the staged
 *   output as a panorama. We intentionally write the metadata ourselves
 *   instead of pulling in a heavy XMP library.
 */

export interface GPanoMeta {
  /** Width of the rendered (cropped) panorama image in pixels. */
  croppedWidth: number;
  /** Height of the rendered (cropped) panorama image in pixels. */
  croppedHeight: number;
  /** Width of the full 360° panorama (usually equals croppedWidth for 2:1 images). */
  fullWidth: number;
  /** Height of the full 360° panorama (usually equals croppedHeight for 2:1 images). */
  fullHeight: number;
  /** Horizontal offset in pixels of the cropped area inside the full panorama. */
  croppedLeft: number;
  /** Vertical offset in pixels of the cropped area inside the full panorama. */
  croppedTop: number;
}

const XMP_NAMESPACE_PREFIX = 'http://ns.adobe.com/xap/1.0/\0';

// ---------------------------------------------------------------------------
// Data URL <-> bytes helpers
// ---------------------------------------------------------------------------

const dataUrlToBytes = (dataUrl: string): { bytes: Uint8Array; mime: string } => {
  const match = dataUrl.match(/^data:([^;]+);base64,(.+)$/);
  if (!match) throw new Error('Not a base64 data URL');
  const mime = match[1];
  const binary = atob(match[2]);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i++) bytes[i] = binary.charCodeAt(i);
  return { bytes, mime };
};

const bytesToDataUrl = (bytes: Uint8Array, mime: string): string => {
  // Build base64 in chunks to avoid call-stack issues on large images.
  let binary = '';
  const chunkSize = 0x8000;
  for (let i = 0; i < bytes.length; i += chunkSize) {
    binary += String.fromCharCode.apply(
      null,
      Array.from(bytes.subarray(i, Math.min(i + chunkSize, bytes.length)))
    );
  }
  return `data:${mime};base64,${btoa(binary)}`;
};

const blobToBytes = async (blob: Blob): Promise<Uint8Array> =>
  new Uint8Array(await blob.arrayBuffer());

// ---------------------------------------------------------------------------
// CRC32 (used for PNG chunks)
// ---------------------------------------------------------------------------

let crcTable: Uint32Array | null = null;
const getCrcTable = (): Uint32Array => {
  if (crcTable) return crcTable;
  const table = new Uint32Array(256);
  for (let n = 0; n < 256; n++) {
    let c = n;
    for (let k = 0; k < 8; k++) {
      c = (c & 1) ? (0xedb88320 ^ (c >>> 1)) : (c >>> 1);
    }
    table[n] = c >>> 0;
  }
  crcTable = table;
  return table;
};

const crc32 = (bytes: Uint8Array): number => {
  const table = getCrcTable();
  let crc = 0xffffffff;
  for (let i = 0; i < bytes.length; i++) {
    crc = (table[(crc ^ bytes[i]) & 0xff] ^ (crc >>> 8)) >>> 0;
  }
  return (crc ^ 0xffffffff) >>> 0;
};

// ---------------------------------------------------------------------------
// XMP packet builder
// ---------------------------------------------------------------------------

export const buildGPanoXmpPacket = (meta: GPanoMeta): string => {
  const {
    croppedWidth,
    croppedHeight,
    fullWidth,
    fullHeight,
    croppedLeft,
    croppedTop,
  } = meta;

  // Standard sidecar packet wrapper expected by JPEG XMP readers. PNG iTXt
  // payloads accept the same packet body, so we use one builder for both.
  return (
    `<?xpacket begin="\uFEFF" id="W5M0MpCehiHzreSzNTczkc9d"?>\n` +
    `<x:xmpmeta xmlns:x="adobe:ns:meta/" x:xmptk="rehub-studio">\n` +
    `  <rdf:RDF xmlns:rdf="http://www.w3.org/1999/02/22-rdf-syntax-ns#">\n` +
    `    <rdf:Description rdf:about=""\n` +
    `      xmlns:GPano="http://ns.google.com/photos/1.0/panorama/"\n` +
    `      GPano:UsePanoramaViewer="True"\n` +
    `      GPano:ProjectionType="equirectangular"\n` +
    `      GPano:CroppedAreaImageWidthPixels="${croppedWidth}"\n` +
    `      GPano:CroppedAreaImageHeightPixels="${croppedHeight}"\n` +
    `      GPano:FullPanoWidthPixels="${fullWidth}"\n` +
    `      GPano:FullPanoHeightPixels="${fullHeight}"\n` +
    `      GPano:CroppedAreaLeftPixels="${croppedLeft}"\n` +
    `      GPano:CroppedAreaTopPixels="${croppedTop}"/>\n` +
    `  </rdf:RDF>\n` +
    `</x:xmpmeta>\n` +
    `<?xpacket end="w"?>`
  );
};

// ---------------------------------------------------------------------------
// XMP parser (extracts GPano fields from a serialised packet)
// ---------------------------------------------------------------------------

const readNumberAttr = (xmp: string, attr: string): number | null => {
  // Match either GPano:Attr="123" or <GPano:Attr>123</GPano:Attr>
  const attrRegex = new RegExp(`GPano:${attr}\\s*=\\s*"([^"]+)"`);
  const elemRegex = new RegExp(`<GPano:${attr}>([^<]+)</GPano:${attr}>`);
  const m = xmp.match(attrRegex) || xmp.match(elemRegex);
  if (!m) return null;
  const n = Number(m[1]);
  return Number.isFinite(n) ? n : null;
};

const parseGPanoXmp = (xmp: string): Partial<GPanoMeta> | null => {
  if (!/GPano:/i.test(xmp)) return null;
  const result: Partial<GPanoMeta> = {};
  const cw = readNumberAttr(xmp, 'CroppedAreaImageWidthPixels');
  const ch = readNumberAttr(xmp, 'CroppedAreaImageHeightPixels');
  const fw = readNumberAttr(xmp, 'FullPanoWidthPixels');
  const fh = readNumberAttr(xmp, 'FullPanoHeightPixels');
  const cl = readNumberAttr(xmp, 'CroppedAreaLeftPixels');
  const ct = readNumberAttr(xmp, 'CroppedAreaTopPixels');
  if (cw != null) result.croppedWidth = cw;
  if (ch != null) result.croppedHeight = ch;
  if (fw != null) result.fullWidth = fw;
  if (fh != null) result.fullHeight = fh;
  if (cl != null) result.croppedLeft = cl;
  if (ct != null) result.croppedTop = ct;
  return result;
};

// ---------------------------------------------------------------------------
// JPEG read / inject (APP1 segment)
// ---------------------------------------------------------------------------

const isJpeg = (bytes: Uint8Array): boolean =>
  bytes.length > 3 && bytes[0] === 0xff && bytes[1] === 0xd8;

const extractXmpFromJpeg = (bytes: Uint8Array): string | null => {
  if (!isJpeg(bytes)) return null;
  const decoder = new TextDecoder('latin1');
  let i = 2;
  while (i < bytes.length - 4) {
    if (bytes[i] !== 0xff) break;
    const marker = bytes[i + 1];
    if (marker === 0xda) break; // start of scan
    const segLen = (bytes[i + 2] << 8) | bytes[i + 3];
    if (segLen < 2) break;
    if (marker === 0xe1) {
      const segStart = i + 4;
      const segEnd = i + 2 + segLen;
      const headerStr = decoder.decode(bytes.subarray(segStart, Math.min(segStart + 29, segEnd)));
      if (headerStr.startsWith('http://ns.adobe.com/xap/1.0/')) {
        // Skip the namespace identifier + null terminator.
        const xmpStart = segStart + 29;
        const xmpBytes = bytes.subarray(xmpStart, segEnd);
        return new TextDecoder('utf-8').decode(xmpBytes);
      }
    }
    i = i + 2 + segLen;
  }
  return null;
};

const injectGPanoIntoJpeg = (bytes: Uint8Array, xmp: string): Uint8Array => {
  if (!isJpeg(bytes)) return bytes;

  // Strip any existing standard XMP APP1 segment so we don't write duplicates.
  const decoder = new TextDecoder('latin1');
  const segments: { start: number; end: number; keep: boolean }[] = [];
  let i = 2;
  while (i < bytes.length - 4) {
    if (bytes[i] !== 0xff) break;
    const marker = bytes[i + 1];
    if (marker === 0xda) {
      // Everything from here to EOI is image data; keep as-is.
      segments.push({ start: i, end: bytes.length, keep: true });
      break;
    }
    const segLen = (bytes[i + 2] << 8) | bytes[i + 3];
    if (segLen < 2) break;
    const segEnd = i + 2 + segLen;
    let keep = true;
    if (marker === 0xe1) {
      const headerStr = decoder.decode(bytes.subarray(i + 4, Math.min(i + 4 + 29, segEnd)));
      if (headerStr.startsWith('http://ns.adobe.com/xap/1.0/')) {
        keep = false;
      }
    }
    segments.push({ start: i, end: segEnd, keep });
    i = segEnd;
  }

  // Build the new XMP APP1 segment.
  const xmpBytes = new TextEncoder().encode(xmp);
  const headerBytes = new TextEncoder().encode(XMP_NAMESPACE_PREFIX);
  const segPayloadLen = headerBytes.length + xmpBytes.length;
  const segLen = segPayloadLen + 2; // length field includes itself
  if (segLen > 0xffff) {
    // Extended XMP would be required; for our payload (~600 bytes) this never
    // triggers, but we fail loud if it ever does.
    throw new Error('XMP payload too large for a single JPEG APP1 segment');
  }
  const segment = new Uint8Array(segPayloadLen + 4);
  segment[0] = 0xff;
  segment[1] = 0xe1;
  segment[2] = (segLen >> 8) & 0xff;
  segment[3] = segLen & 0xff;
  segment.set(headerBytes, 4);
  segment.set(xmpBytes, 4 + headerBytes.length);

  // Reassemble: SOI + new XMP segment + (kept original segments).
  const totalKeptLen = segments.reduce((acc, s) => (s.keep ? acc + (s.end - s.start) : acc), 0);
  const out = new Uint8Array(2 + segment.length + totalKeptLen);
  out[0] = 0xff;
  out[1] = 0xd8;
  let offset = 2;
  out.set(segment, offset);
  offset += segment.length;
  for (const s of segments) {
    if (!s.keep) continue;
    out.set(bytes.subarray(s.start, s.end), offset);
    offset += s.end - s.start;
  }
  return out;
};

// ---------------------------------------------------------------------------
// PNG read / inject (iTXt chunk with keyword "XML:com.adobe.xmp")
// ---------------------------------------------------------------------------

const PNG_SIGNATURE = [0x89, 0x50, 0x4e, 0x47, 0x0d, 0x0a, 0x1a, 0x0a];
const PNG_XMP_KEYWORD = 'XML:com.adobe.xmp';

const isPng = (bytes: Uint8Array): boolean => {
  if (bytes.length < 8) return false;
  for (let i = 0; i < 8; i++) if (bytes[i] !== PNG_SIGNATURE[i]) return false;
  return true;
};

const readUint32 = (bytes: Uint8Array, offset: number): number =>
  ((bytes[offset] << 24) | (bytes[offset + 1] << 16) | (bytes[offset + 2] << 8) | bytes[offset + 3]) >>> 0;

const writeUint32 = (out: Uint8Array, offset: number, value: number): void => {
  out[offset] = (value >>> 24) & 0xff;
  out[offset + 1] = (value >>> 16) & 0xff;
  out[offset + 2] = (value >>> 8) & 0xff;
  out[offset + 3] = value & 0xff;
};

interface PngChunk {
  start: number;
  end: number;
  type: string;
  dataStart: number;
  dataEnd: number;
}

const iteratePngChunks = (bytes: Uint8Array): PngChunk[] => {
  const chunks: PngChunk[] = [];
  let i = 8;
  const decoder = new TextDecoder('latin1');
  while (i < bytes.length) {
    if (i + 8 > bytes.length) break;
    const length = readUint32(bytes, i);
    const type = decoder.decode(bytes.subarray(i + 4, i + 8));
    const dataStart = i + 8;
    const dataEnd = dataStart + length;
    const end = dataEnd + 4; // include CRC
    chunks.push({ start: i, end, type, dataStart, dataEnd });
    if (type === 'IEND') break;
    i = end;
  }
  return chunks;
};

const extractXmpFromPng = (bytes: Uint8Array): string | null => {
  if (!isPng(bytes)) return null;
  const chunks = iteratePngChunks(bytes);
  const decoder = new TextDecoder('utf-8');
  for (const c of chunks) {
    if (c.type !== 'iTXt' && c.type !== 'tEXt') continue;
    const data = bytes.subarray(c.dataStart, c.dataEnd);
    // Keyword is null-terminated.
    let nullIdx = 0;
    while (nullIdx < data.length && data[nullIdx] !== 0) nullIdx++;
    const keyword = new TextDecoder('latin1').decode(data.subarray(0, nullIdx));
    if (keyword !== PNG_XMP_KEYWORD) continue;
    if (c.type === 'tEXt') {
      return decoder.decode(data.subarray(nullIdx + 1));
    }
    // iTXt layout: keyword\0 compressionFlag(1) compressionMethod(1)
    // languageTag\0 translatedKeyword\0 text
    let p = nullIdx + 1;
    if (p + 2 > data.length) continue;
    const compressionFlag = data[p];
    p += 2; // skip flag + method
    while (p < data.length && data[p] !== 0) p++;
    p++; // skip language tag null
    while (p < data.length && data[p] !== 0) p++;
    p++; // skip translated keyword null
    if (compressionFlag !== 0) continue; // not handling compressed XMP
    return decoder.decode(data.subarray(p));
  }
  return null;
};

const injectGPanoIntoPng = (bytes: Uint8Array, xmp: string): Uint8Array => {
  if (!isPng(bytes)) return bytes;

  // Build the iTXt chunk payload.
  const keywordBytes = new TextEncoder().encode(PNG_XMP_KEYWORD);
  const xmpBytes = new TextEncoder().encode(xmp);
  // keyword\0 + compressionFlag(0) + compressionMethod(0) + languageTag\0 +
  // translatedKeyword\0 + text
  const payload = new Uint8Array(keywordBytes.length + 1 + 1 + 1 + 1 + 1 + xmpBytes.length);
  let p = 0;
  payload.set(keywordBytes, p); p += keywordBytes.length;
  payload[p++] = 0; // null terminator after keyword
  payload[p++] = 0; // compression flag
  payload[p++] = 0; // compression method
  payload[p++] = 0; // empty language tag null terminator
  payload[p++] = 0; // empty translated keyword null terminator
  payload.set(xmpBytes, p);

  // Type bytes "iTXt".
  const typeBytes = new Uint8Array([0x69, 0x54, 0x58, 0x74]);

  // CRC over type + payload.
  const crcInput = new Uint8Array(typeBytes.length + payload.length);
  crcInput.set(typeBytes, 0);
  crcInput.set(payload, typeBytes.length);
  const crc = crc32(crcInput);

  const chunk = new Uint8Array(4 + 4 + payload.length + 4);
  writeUint32(chunk, 0, payload.length);
  chunk.set(typeBytes, 4);
  chunk.set(payload, 8);
  writeUint32(chunk, 8 + payload.length, crc);

  // Strip any existing XMP iTXt chunk and insert the new one right after IHDR.
  const chunks = iteratePngChunks(bytes);
  const decoder = new TextDecoder('latin1');
  const keep: PngChunk[] = [];
  for (const c of chunks) {
    if (c.type === 'iTXt' || c.type === 'tEXt') {
      const data = bytes.subarray(c.dataStart, c.dataEnd);
      let nullIdx = 0;
      while (nullIdx < data.length && data[nullIdx] !== 0) nullIdx++;
      const keyword = decoder.decode(data.subarray(0, nullIdx));
      if (keyword === PNG_XMP_KEYWORD) continue;
    }
    keep.push(c);
  }

  const ihdrIndex = keep.findIndex((c) => c.type === 'IHDR');
  if (ihdrIndex === -1) return bytes;

  const totalLen =
    8 + // PNG signature
    keep.reduce((acc, c) => acc + (c.end - c.start), 0) +
    chunk.length;

  const out = new Uint8Array(totalLen);
  out.set(PNG_SIGNATURE, 0);
  let offset = 8;
  for (let idx = 0; idx < keep.length; idx++) {
    const c = keep[idx];
    out.set(bytes.subarray(c.start, c.end), offset);
    offset += c.end - c.start;
    if (idx === ihdrIndex) {
      out.set(chunk, offset);
      offset += chunk.length;
    }
  }
  return out;
};

// ---------------------------------------------------------------------------
// Public API
// ---------------------------------------------------------------------------

/** Read GPano metadata from a File or Blob. Returns null if none is present. */
export const readGPanoXmp = async (
  file: File | Blob
): Promise<Partial<GPanoMeta> | null> => {
  try {
    const bytes = await blobToBytes(file);
    let xmp: string | null = null;
    if (isJpeg(bytes)) xmp = extractXmpFromJpeg(bytes);
    else if (isPng(bytes)) xmp = extractXmpFromPng(bytes);
    if (!xmp) return null;
    return parseGPanoXmp(xmp);
  } catch (err) {
    console.warn('readGPanoXmp failed:', err);
    return null;
  }
};

/**
 * Build a complete GPanoMeta for a 2:1 equirectangular image, using any
 * source metadata as a hint and falling back to the rendered dimensions.
 */
export const resolveGPanoMeta = (
  renderedWidth: number,
  renderedHeight: number,
  source?: Partial<GPanoMeta> | null
): GPanoMeta => {
  // For our pipeline we always render the full panorama, so cropped == full.
  const fullWidth = source?.fullWidth ?? renderedWidth;
  const fullHeight = source?.fullHeight ?? renderedHeight;
  return {
    croppedWidth: renderedWidth,
    croppedHeight: renderedHeight,
    fullWidth,
    fullHeight,
    croppedLeft: source?.croppedLeft ?? 0,
    croppedTop: source?.croppedTop ?? 0,
  };
};

/** Inject GPano XMP into a raw byte array (PNG or JPEG). */
export const injectGPanoIntoBytes = (
  bytes: Uint8Array,
  meta: GPanoMeta
): Uint8Array => {
  const xmp = buildGPanoXmpPacket(meta);
  if (isPng(bytes)) return injectGPanoIntoPng(bytes, xmp);
  if (isJpeg(bytes)) return injectGPanoIntoJpeg(bytes, xmp);
  return bytes;
};

/** Inject GPano XMP into a base64 data URL and return a new data URL. */
export const dataUrlWithGPano = (dataUrl: string, meta: GPanoMeta): string => {
  const { bytes, mime } = dataUrlToBytes(dataUrl);
  const out = injectGPanoIntoBytes(bytes, meta);
  return bytesToDataUrl(out, mime);
};

/** Inject GPano XMP into a Blob (for download paths). Returns a new Blob. */
export const blobWithGPano = async (
  blob: Blob,
  meta: GPanoMeta
): Promise<Blob> => {
  const bytes = await blobToBytes(blob);
  const out = injectGPanoIntoBytes(bytes, meta);
  // Cast through ArrayBuffer to satisfy strict BlobPart typings (Uint8Array's
  // backing buffer can be a SharedArrayBuffer in some lib targets).
  return new Blob([out.buffer.slice(out.byteOffset, out.byteOffset + out.byteLength) as ArrayBuffer], { type: blob.type });
};

// ---------------------------------------------------------------------------
// Polar region restore (eliminates the "black circle on the floor/ceiling"
// artifact caused by 21:9 black-bar padding leaking into Gemini's output)
// ---------------------------------------------------------------------------

const loadImage = (src: string): Promise<HTMLImageElement> =>
  new Promise((resolve, reject) => {
    const img = new Image();
    if (!src.startsWith('data:')) img.crossOrigin = 'anonymous';
    img.onload = () => resolve(img);
    img.onerror = (e) => reject(e);
    img.src = src;
  });

export interface RestorePolarOptions {
  /** Fraction of image height to fully replace at the top (zenith). Default 0.07 (7%). */
  topFraction?: number;
  /** Fraction of image height to fully replace at the bottom (nadir). Default 0.07 (7%). */
  bottomFraction?: number;
  /** Fraction of image height used to alpha-blend between source and staged. Default 0.02. */
  blendFraction?: number;
  /**
   * Fraction of source height to skip at the very top/bottom when sampling
   * source pixels. Most 360 cameras leave a dark zenith/nadir ring in their
   * top/bottom rows (lens shadow, tripod, etc.). Skipping that region prevents
   * the artifact from being preserved in the staged output. Default 0.02 (2%).
   */
  artifactSkipFraction?: number;
  /**
   * Output encoding. Default 'image/png' (lossless) to preserve previous
   * behaviour. Pass 'image/jpeg' for full-resolution panoramas where a
   * lossless PNG would balloon to tens of MB.
   */
  outputType?: 'image/png' | 'image/jpeg';
  /** JPEG quality (0..1). Ignored for PNG. Default 0.92. */
  outputQuality?: number;
}

/**
 * Replace the top and bottom polar bands of a staged 2:1 equirectangular
 * panorama with the original pixels from the source, blending across a small
 * transition band so the seam is invisible.
 *
 * Two artifacts this fixes:
 *   1. Dark "leakage" at the crop boundary from our 21:9 black-bar padding
 *      workaround (now obviated by edge-replicate padding, but belt-and-braces).
 *   2. Source-side camera artifacts (zenith/nadir shadows) — we skip the very
 *      top/bottom rows of the source and shift content down so the polar region
 *      of the output never contains those rows.
 */
export const restorePolarRegions = async (
  stagedDataUrl: string,
  sourceDataUrl: string,
  options: RestorePolarOptions = {}
): Promise<string> => {
  const topFraction = options.topFraction ?? 0.07;
  const bottomFraction = options.bottomFraction ?? 0.07;
  const blendFraction = options.blendFraction ?? 0.02;
  const artifactSkipFraction = options.artifactSkipFraction ?? 0.02;
  const outputType = options.outputType ?? 'image/png';
  const outputQuality = options.outputQuality ?? 0.92;

  const [staged, source] = await Promise.all([
    loadImage(stagedDataUrl),
    loadImage(sourceDataUrl),
  ]);

  const W = staged.width;
  const H = staged.height;
  if (W === 0 || H === 0) return stagedDataUrl;

  const canvas = document.createElement('canvas');
  canvas.width = W;
  canvas.height = H;
  const ctx = canvas.getContext('2d');
  if (!ctx) return stagedDataUrl;

  ctx.imageSmoothingEnabled = true;
  ctx.imageSmoothingQuality = 'high';

  // Base layer: the staged image. Subsequent draws overlay onto this.
  ctx.drawImage(staged, 0, 0, W, H);

  // A fraction of 0 means "skip this pole entirely" (used for tripod-removal
  // where we want Gemini's nadir edit to survive). Only clamp to 1px when the
  // caller asked for a positive band.
  const topBand = topFraction > 0 ? Math.max(1, Math.round(H * topFraction)) : 0;
  const bottomBand = bottomFraction > 0 ? Math.max(1, Math.round(H * bottomFraction)) : 0;
  const blendPx = blendFraction > 0 ? Math.max(1, Math.round(H * blendFraction)) : 0;
  const skipTopPx = Math.max(0, Math.round(source.height * artifactSkipFraction));
  const skipBotPx = Math.max(0, Math.round(source.height * artifactSkipFraction));

  // === TOP POLE ============================================================
  if (topBand > 0) {
    // Source rows [skipTopPx, skipTopPx + topBand) stretched into staged rows
    // [0, topBand). The skip ensures the camera's zenith artifact (typically
    // dark pixels in the top 1-2% of the source) never lands on the staged pole.
    ctx.drawImage(
      source,
      0, skipTopPx, source.width, topBand,
      0, 0, W, topBand
    );

    // Transition rows [topBand, topBand+blendPx): linearly fade source→staged.
    // Underlying canvas at these rows is `staged` (base layer). We paint
    // source-shifted on top with decreasing alpha so the result is
    //   alpha*source_shifted + (1-alpha)*staged
    // which smoothly transitions from source-leaning at row topBand to
    // staged-leaning at row topBand+blendPx-1.
    for (let i = 0; i < blendPx; i++) {
      const y = topBand + i;
      if (y >= H) break;
      const alpha = 1 - (i + 1) / (blendPx + 1); // source weight: ~1 -> ~0
      ctx.globalAlpha = alpha;
      ctx.drawImage(
        source,
        0, skipTopPx + topBand + i, source.width, 1,
        0, y, W, 1
      );
    }
    ctx.globalAlpha = 1;
  }

  // === BOTTOM POLE =========================================================
  if (bottomBand > 0) {
    // Mirror of the top: source rows
    // [source.height - skipBotPx - bottomBand, source.height - skipBotPx)
    // stretched into staged rows [H - bottomBand, H).
    ctx.drawImage(
      source,
      0, source.height - skipBotPx - bottomBand, source.width, bottomBand,
      0, H - bottomBand, W, bottomBand
    );

    // Transition rows [H-bottomBand-blendPx, H-bottomBand): fade source→staged
    // moving up.
    for (let i = 0; i < blendPx; i++) {
      const y = H - bottomBand - 1 - i;
      if (y < 0) break;
      const alpha = 1 - (i + 1) / (blendPx + 1); // source weight: ~1 -> ~0
      ctx.globalAlpha = alpha;
      ctx.drawImage(
        source,
        0, source.height - skipBotPx - bottomBand - 1 - i, source.width, 1,
        0, y, W, 1
      );
    }
    ctx.globalAlpha = 1;
  }

  return outputType === 'image/jpeg'
    ? canvas.toDataURL('image/jpeg', outputQuality)
    : canvas.toDataURL('image/png');
};

// ---------------------------------------------------------------------------
// Resolution-preserving composite (original full-res + staged furniture only)
// ---------------------------------------------------------------------------

export interface CompositeStagedOptions {
  /**
   * Working width for the difference mask. The mask is low-frequency, so it is
   * computed at this reduced width and then smoothly upscaled to full
   * resolution — far cheaper than a full-res pixel diff and the upscale adds
   * natural feathering. Default 2048.
   */
  maskWidth?: number;
  /**
   * Per-pixel colour distance (sum of |dR|+|dG|+|dB|, range 0..765) below which
   * a pixel is treated as "unchanged" (keep original). Default 24.
   */
  diffLow?: number;
  /** Distance at or above which a pixel is treated as "fully staged". Default 80. */
  diffHigh?: number;
  /** Output encoding. Default 'image/jpeg'. */
  outputType?: 'image/png' | 'image/jpeg';
  /** JPEG quality (0..1). Ignored for PNG. Default 0.95. */
  outputQuality?: number;
}

const clamp01 = (v: number): number => (v < 0 ? 0 : v > 1 ? 1 : v);

/**
 * Separable box blur (radius r) over a single-channel Float32 buffer.
 * X axis wraps (equirectangular seam); Y axis clamps (poles).
 */
const boxBlurWrapX = (
  src: Float32Array,
  w: number,
  h: number,
  r: number
): Float32Array => {
  if (r < 1) return src;
  const tmp = new Float32Array(w * h);
  const out = new Float32Array(w * h);
  const win = r * 2 + 1;
  // Horizontal pass (wrap in X).
  for (let y = 0; y < h; y++) {
    const row = y * w;
    let sum = 0;
    for (let k = -r; k <= r; k++) {
      const x = ((k % w) + w) % w;
      sum += src[row + x];
    }
    for (let x = 0; x < w; x++) {
      tmp[row + x] = sum / win;
      const outX = ((x - r) % w + w) % w;
      const inX = ((x + r + 1) % w + w) % w;
      sum += src[row + inX] - src[row + outX];
    }
  }
  // Vertical pass (clamp in Y).
  for (let x = 0; x < w; x++) {
    let sum = 0;
    for (let k = -r; k <= r; k++) {
      const y = Math.min(h - 1, Math.max(0, k));
      sum += tmp[y * w + x];
    }
    for (let y = 0; y < h; y++) {
      out[y * w + x] = sum / win;
      const outY = Math.min(h - 1, Math.max(0, y - r));
      const inY = Math.min(h - 1, Math.max(0, y + r + 1));
      sum += tmp[inY * w + x] - tmp[outY * w + x];
    }
  }
  return out;
};

/**
 * Grow (max-filter / dilate) a single-channel mask by `r` pixels so the feather
 * also covers contact shadows and soft furniture edges just outside the strict
 * colour-difference region. X wraps, Y clamps.
 */
const dilateWrapX = (
  src: Float32Array,
  w: number,
  h: number,
  r: number
): Float32Array => {
  if (r < 1) return src;
  const out = new Float32Array(w * h);
  for (let y = 0; y < h; y++) {
    const row = y * w;
    for (let x = 0; x < w; x++) {
      let m = 0;
      for (let k = -r; k <= r; k++) {
        const xx = ((x + k) % w + w) % w;
        const v = src[row + xx];
        if (v > m) m = v;
      }
      out[row + x] = m;
    }
  }
  return out;
};

/**
 * Composite a Gemini-staged panorama back over the FULL-RESOLUTION original so
 * that every surface the model was told to preserve (walls, floor, ceiling,
 * windows, doors, fixtures) keeps the original camera's pixels at full
 * resolution, while only the regions the model actually changed (added
 * furniture, rugs, curtains, regenerated reflections, repaired nadir) come from
 * the staged output.
 *
 * Why this exists: `gemini-3-pro-image-preview` caps output at 4K
 * (~4096×2048 for a 2:1 pano), so a staged panorama is always lower resolution
 * than the 8K source a 360 camera produces — it looks soft when you zoom into a
 * small field of view in the tour viewer. Since virtual staging only *adds*
 * furniture to an otherwise-preserved room, we can restore full resolution
 * everywhere except the furniture by pasting the original pixels back wherever
 * the staged result barely changed.
 *
 * Approach:
 *   1. Build a feathered "what changed" mask from the colour difference between
 *      the original and the staged result (computed at reduced resolution).
 *   2. Upscale the staged result to the original's resolution.
 *   3. result = original·(1−mask) + stagedUpscaled·mask.
 *
 * Assumes the staged colours have already been white-balanced to the original
 * (the pipeline runs `removeGlobalTint` first) so kept/staged regions blend
 * without a visible tonal seam. If the original is not meaningfully larger than
 * the staged result, the staged result is returned unchanged (no benefit).
 */
export const compositeStagedOverOriginal = async (
  originalDataUrl: string,
  stagedDataUrl: string,
  options: CompositeStagedOptions = {}
): Promise<string> => {
  const diffLow = options.diffLow ?? 24;
  const diffHigh = options.diffHigh ?? 80;
  const outputType = options.outputType ?? 'image/jpeg';
  const outputQuality = options.outputQuality ?? 0.95;

  const [original, staged] = await Promise.all([
    loadImage(originalDataUrl),
    loadImage(stagedDataUrl),
  ]);

  const origW = original.width;
  const origH = original.height;
  const stagedW = staged.width;
  if (origW === 0 || origH === 0 || stagedW === 0) return stagedDataUrl;

  // No resolution to recover if the original isn't meaningfully larger.
  if (origW <= stagedW * 1.02) return stagedDataUrl;

  // --- 1. Difference mask at reduced resolution ---------------------------
  const mW = Math.max(64, Math.min(options.maskWidth ?? 2048, origW));
  const mH = Math.max(32, Math.round((mW * origH) / origW));

  const sample = (img: HTMLImageElement): Uint8ClampedArray | null => {
    const c = document.createElement('canvas');
    c.width = mW;
    c.height = mH;
    const cx = c.getContext('2d', { willReadFrequently: true });
    if (!cx) return null;
    cx.imageSmoothingEnabled = true;
    cx.imageSmoothingQuality = 'high';
    cx.drawImage(img, 0, 0, mW, mH);
    return cx.getImageData(0, 0, mW, mH).data;
  };

  const origData = sample(original);
  const stagedData = sample(staged);
  if (!origData || !stagedData) return stagedDataUrl;

  const span = Math.max(1, diffHigh - diffLow);
  let mask: Float32Array = new Float32Array(mW * mH);
  for (let i = 0, p = 0; i < mask.length; i++, p += 4) {
    const d =
      Math.abs(origData[p] - stagedData[p]) +
      Math.abs(origData[p + 1] - stagedData[p + 1]) +
      Math.abs(origData[p + 2] - stagedData[p + 2]);
    mask[i] = clamp01((d - diffLow) / span); // 0 = keep original, 1 = use staged
  }

  // Grow to catch contact shadows / soft edges, then feather the transition.
  const dilateR = Math.max(2, Math.round(mW / 512));
  const blurR = Math.max(3, Math.round(mW / 256));
  mask = dilateWrapX(mask, mW, mH, dilateR);
  mask = boxBlurWrapX(mask, mW, mH, blurR);

  // Pack the feathered mask into the ALPHA channel of a small canvas so we can
  // upscale it (with smoothing) and use it as a compositing stencil.
  const maskCanvas = document.createElement('canvas');
  maskCanvas.width = mW;
  maskCanvas.height = mH;
  const maskCtx = maskCanvas.getContext('2d');
  if (!maskCtx) return stagedDataUrl;
  const maskImg = maskCtx.createImageData(mW, mH);
  for (let i = 0, p = 0; i < mask.length; i++, p += 4) {
    maskImg.data[p] = 255;
    maskImg.data[p + 1] = 255;
    maskImg.data[p + 2] = 255;
    maskImg.data[p + 3] = Math.round(clamp01(mask[i]) * 255);
  }
  maskCtx.putImageData(maskImg, 0, 0);

  // --- 2. Staged upscaled to full res, masked to "changed" regions --------
  const outW = origW;
  const outH = origH;
  const stagedFull = document.createElement('canvas');
  stagedFull.width = outW;
  stagedFull.height = outH;
  const sCtx = stagedFull.getContext('2d');
  if (!sCtx) return stagedDataUrl;
  sCtx.imageSmoothingEnabled = true;
  sCtx.imageSmoothingQuality = 'high';
  sCtx.drawImage(staged, 0, 0, outW, outH);
  // Keep staged only where the (smoothly upscaled) mask says it changed.
  sCtx.globalCompositeOperation = 'destination-in';
  sCtx.drawImage(maskCanvas, 0, 0, mW, mH, 0, 0, outW, outH);
  sCtx.globalCompositeOperation = 'source-over';

  // --- 3. Original full-res base + staged furniture on top ----------------
  const out = document.createElement('canvas');
  out.width = outW;
  out.height = outH;
  const oCtx = out.getContext('2d');
  if (!oCtx) return stagedDataUrl;
  oCtx.imageSmoothingEnabled = true;
  oCtx.imageSmoothingQuality = 'high';
  oCtx.drawImage(original, 0, 0, outW, outH);
  oCtx.drawImage(stagedFull, 0, 0);

  return outputType === 'image/png'
    ? out.toDataURL('image/png')
    : out.toDataURL('image/jpeg', outputQuality);
};

/** Convenience: read a File/Blob into a base64 data URL (PNG/JPEG passthrough). */
export const fileToDataUrl = (file: File | Blob): Promise<string> =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onloadend = () => resolve(reader.result as string);
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });

/**
 * Normalise a near-2:1 image to an exact 2:1 aspect ratio by bilinear
 * resize. Bilinear resampling preserves the seamless left/right wrap of
 * an equirectangular panorama (every column is resampled independently
 * along the same vertical line), so the wrap stays seamless. Cropping or
 * letterboxing would either break the wrap or introduce non-pano bands,
 * so resizing is the only safe correction for an off-ratio Gemini result.
 *
 * Output dimensions are chosen to preserve the larger pixel axis of the
 * input — keeps Gemini's full output resolution rather than downscaling.
 *
 * If the input is already exactly 2:1 (within 0.1%), returns the original
 * data URL unchanged so we avoid a needless re-encode.
 */
export const resizeTo2to1 = async (dataUrl: string): Promise<string> => {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => {
      const inRatio = img.width / img.height;
      if (Math.abs(inRatio - 2.0) < 0.001) {
        resolve(dataUrl);
        return;
      }

      let outW: number, outH: number;
      if (img.width >= img.height * 2) {
        // Wider than 2:1 — keep the wider width, derive height.
        outW = img.width;
        outH = Math.round(img.width / 2);
      } else {
        // Taller than 2:1 — keep the height, derive width.
        outH = img.height;
        outW = img.height * 2;
      }

      const canvas = document.createElement('canvas');
      canvas.width = outW;
      canvas.height = outH;
      const ctx = canvas.getContext('2d');
      if (!ctx) {
        reject(new Error('Canvas context error'));
        return;
      }
      ctx.imageSmoothingEnabled = true;
      ctx.imageSmoothingQuality = 'high';
      ctx.drawImage(img, 0, 0, img.width, img.height, 0, 0, outW, outH);
      resolve(canvas.toDataURL('image/png'));
    };
    img.onerror = (e) => reject(e);
    img.src = dataUrl;
  });
};
